import React from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useShipmentState from '../../utils/useShipmentState';
import { Link, useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ShipmentForm from '../../components/ShipmentForm';
import Page from '@afosto/app-components-react/Page';
import getServicePointAddress from '@afosto/utils-react/shipment/getServicePointAddress';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import translations from './translations';
import ShipmentFormProvider from '../../components/ShipmentFormProvider';

const CreateShipment = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:shipments:write',
    'lcs:locations:read',
    'rel:contacts:write',
  ]);

  const history = useHistory();
  const intl = useIntl();
  const shipmentState = useShipmentState();
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Handle creating a new shipment.
   *
   * @param {string} toType
   * @param {string} fromType
   * @param {object} toAddress
   * @param {object} fromAddress
   * @param {object} toLocation
   * @param {object} fromLocation
   * @param {string} type
   * @param {date} expectedAt
   * @param {date} shipAt
   * @param {object} method
   * @param {object} contact
   * @param {array} items
   * @returns {Promise<void>}
   */
  const handleCreateShipment = async ({
    toType,
    fromType,
    toAddress,
    fromAddress,
    toLocation,
    fromLocation,
    type,
    expectedAt,
    shipAt,
    method,
    contact,
    items = [],
  }) => {
    try {
      const formattedItems = items.reduce(
        (acc, item) => [...acc, { ...item, quantity: parseInt(item.quantity, 10) }],
        [],
      );

      const carrierLocationAddressID = await getServicePointAddress(method);

      const toEntity = {
        entity_type: toType,
        entity_id:
          toType === 'location' ? toLocation?.id : carrierLocationAddressID ?? toAddress?.id,
        ...(method?.method?.is_carrier_location
          ? {
              carrier: {
                is_carrier_location: method?.method?.is_carrier_location,
                carrier_location_reference: method?.servicePoint?.id,
              },
            }
          : {}),
      };

      const response = await shipmentsApi.createShipment({
        addressing: {
          to: toEntity,
          from: {
            entity_type: fromType,
            entity_id: fromType === 'location' ? fromLocation?.id : fromAddress?.id,
          },
        },
        items: formattedItems,
        is_outbound: type !== 'inbound' ?? true,
        expected_at: type !== 'outbound' ? expectedAt : null,
        ship_at: type !== 'inbound' ? shipAt : null,
        method_id: method?.method?.id ?? null,
        contact_id: contact?.id ?? null,
      });
      const { data } = response;

      if (type === 'transfer') {
        await shipmentsApi.createShipment({
          addressing: {
            to: {
              entity_type: fromType,
              entity_id: fromType === 'location' ? fromLocation?.id : fromAddress?.id,
            },
            from: toEntity,
          },
          items: formattedItems,
          is_outbound: false,
          expected_at: expectedAt,
          method_id: method?.id ?? null,
          contact_id: contact?.id ?? null,
        });
      }

      history.push(`/shipments/${data.id}`);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.shipmentNotCreated), {
        variant: 'error',
      });
    }
  };

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)}>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Page
      title={intl.formatMessage(translations.title)}
      breadcrumbs={[
        {
          label: intl.formatMessage(translations.breadcrumb.shipments),
          path: '/shipments',
        },
        {
          label: intl.formatMessage(translations.title),
          path: `/shipments/new`,
          active: true,
        },
      ]}
      linkComponent={Link}
    >
      <ShipmentFormProvider value={shipmentState}>
        <ShipmentForm onSubmit={handleCreateShipment} />
      </ShipmentFormProvider>
    </Page>
  );
};

export default CreateShipment;
